/**
 * Canonical model for the `card.source` string.
 *
 * Historically `source` was an ad-hoc string parsed in a dozen places:
 *
 *   - `m:{start}-{end}`     — character-offset range into the deck's (single)
 *                             PDF extraction text
 *   - `{n}` / `{a}-{b}` / `{a},{b}` — legacy AI chunk indices
 *   - `manual`              — no source link
 *   - `notes...`            — card created from the notes editor
 *   - anything else         — opaque legacy values (`v...`, GPT labels, …)
 *
 * Multi-source decks and rectangle-anchored cards extend the grammar with
 * two ADDITIVE forms (old clients simply treat them as "no text anchor",
 * they never crash on them):
 *
 *   - `r:{page}:{x},{y},{w},{h}`         — a normalized rectangle on a PDF
 *     page (coords in 0..1 of the page's extraction width/height, 4 d.p.).
 *     Used by image occlusions cut from regions without text and by any
 *     future region-anchored card.
 *   - `s:{sourceId}:{inner}`             — qualifies `inner` (an `m:`, `r:`
 *     or `c:` ref) to a SPECIFIC entry of `SyncDeckMeta.sources`. Unqualified
 *     refs implicitly belong to the deck's primary source (`sources[0]`),
 *     which is how every pre-multi-source card keeps working unchanged.
 *   - `c:{n}` / `c:{a}-{b}` — a legacy chunk ref in its QUALIFIABLE spelling.
 *     Only ever written inside `s:{sourceId}:c:…`, when a chunk-ref card is
 *     pinned to the document it was drawn from (`primary-pin.ts`): a bare
 *     chunk index has no room for a source id, and the day the primary
 *     changes under it, "the deck's paragraph 3" would mean another file's.
 *
 * THE IMPLICIT PRIMARY IS A STABLE REFERENCE, NOT A SLOT. Every unqualified
 * ref (and a map node or quiz attempt with no source id) means "the document
 * that was `sources[0]` when it was written". So whatever moves index 0 —
 * a batch document landing ahead of a sibling, removing the first document,
 * undoing that removal — first pins the outgoing primary's implicit refs to
 * its id, in the same write (`sync-client/deck-sources.ts`). Old data never
 * needs a migration: a bare ref on a deck whose first document never moved
 * still means what it always did.
 *
 * EVERY consumer should go through {@link parseCardSourceRef} instead of
 * string-splitting — this module is the single owner of the grammar.
 */

export interface NormalizedRect {
  /** Left edge, 0..1 of page width. */
  x: number;
  /** Top edge, 0..1 of page height. */
  y: number;
  /** Width, 0..1 of page width. */
  w: number;
  /** Height, 0..1 of page height. */
  h: number;
}

export type CardSourceRef =
  | {
      type: 'text-range';
      /** Explicit source id when qualified via `s:`; undefined = primary. */
      sourceId?: string;
      start: number;
      end: number;
    }
  | {
      type: 'page-rect';
      sourceId?: string;
      /** 1-based extraction page number. */
      pageNumber: number;
      rect: NormalizedRect;
    }
  | {
      type: 'chunk';
      /** Explicit source id when pinned via `s:{id}:c:…`; undefined = primary. */
      sourceId?: string;
      start: number;
      end: number;
    }
  | { type: 'notes'; raw: string }
  | { type: 'manual' }
  | { type: 'opaque'; raw: string };

const TEXT_RANGE_RE = /^m:(\d+)-(\d+)$/;
const CHUNK_SINGLE_RE = /^\d+$/;
// Two or more `-`/`,`-separated indices. LLMs were told "{n},{n} or {n}"
// but historical data also carries drifted shapes like `1,2,3` or `2-4,6`;
// the legacy splitters tolerated them, so the grammar spans first..last.
const CHUNK_LIST_RE = /^(\d+)(?:\s*[-,]\s*\d+)*\s*[-,]\s*(\d+)$/;
const RECT_RE = /^r:(\d+):(-?[\d.]+),(-?[\d.]+),(-?[\d.]+),(-?[\d.]+)$/;
// The pinned spelling of a chunk ref — only ever inside `s:{sourceId}:`.
const QUALIFIED_CHUNK_RE = /^c:(\d+)(?:-(\d+))?$/;
const QUALIFIED_PREFIX = 's:';

const round4 = (n: number): number => Math.round(n * 10000) / 10000;

const clamp01 = (n: number): number => (n < 0 ? 0 : n > 1 ? 1 : n);

function parseInner(inner: string, sourceId: string | undefined): CardSourceRef | null {
  const textMatch = TEXT_RANGE_RE.exec(inner);
  if (textMatch) {
    return {
      type: 'text-range',
      sourceId,
      start: Number(textMatch[1]),
      end: Number(textMatch[2]),
    };
  }
  const rectMatch = RECT_RE.exec(inner);
  if (rectMatch) {
    const [, page, x, y, w, h] = rectMatch;
    const rect = { x: Number(x), y: Number(y), w: Number(w), h: Number(h) };
    if ([rect.x, rect.y, rect.w, rect.h].some((v) => !Number.isFinite(v))) {
      return null;
    }
    return {
      type: 'page-rect',
      sourceId,
      pageNumber: Number(page),
      rect,
    };
  }
  return null;
}

/** Parse any historical or current `card.source` value. Never throws. */
export function parseCardSourceRef(source: string | null | undefined): CardSourceRef {
  if (!source) return { type: 'manual' };
  const trimmed = source.trim();
  if (!trimmed || trimmed === 'manual' || trimmed === 'MANUAL') {
    return { type: 'manual' };
  }
  if (trimmed.startsWith('notes')) {
    return { type: 'notes', raw: trimmed };
  }

  if (trimmed.startsWith(QUALIFIED_PREFIX)) {
    // `s:{sourceId}:{inner}` — sourceId is a uuid/deck key today, but be
    // defensive and locate the inner ref by its `:m:` / `:r:` marker so a
    // sourceId containing `:` can't shift the parse.
    const rest = trimmed.slice(QUALIFIED_PREFIX.length);
    const mIdx = rest.indexOf(':m:');
    const rIdx = rest.indexOf(':r:');
    const cIdx = rest.indexOf(':c:');
    const candidates = [mIdx, rIdx, cIdx].filter((i) => i >= 0);
    if (candidates.length > 0) {
      const cut = Math.min(...candidates);
      const sourceId = rest.slice(0, cut);
      const inner = rest.slice(cut + 1);
      if (sourceId) {
        const parsed = parseInner(inner, sourceId);
        if (parsed) return parsed;
        const chunk = QUALIFIED_CHUNK_RE.exec(inner);
        if (chunk) {
          const start = Number(chunk[1]);
          return {
            type: 'chunk',
            sourceId,
            start,
            end: chunk[2] === undefined ? start : Number(chunk[2]),
          };
        }
      }
    }
    return { type: 'opaque', raw: trimmed };
  }

  const direct = parseInner(trimmed, undefined);
  if (direct) return direct;

  if (CHUNK_SINGLE_RE.test(trimmed)) {
    const n = Number(trimmed);
    return { type: 'chunk', start: n, end: n };
  }
  const chunkRange = CHUNK_LIST_RE.exec(trimmed);
  if (chunkRange) {
    return {
      type: 'chunk',
      start: Number(chunkRange[1]),
      end: Number(chunkRange[2]),
    };
  }

  return { type: 'opaque', raw: trimmed };
}

/** Upper bound on {@link parseCardSourceRefCached} entries (oldest evicted first). */
export const CARD_SOURCE_REF_CACHE_MAX = 20_000;

const refCache = new Map<string, CardSourceRef>();

// Written as the literal `process.env.NODE_ENV` so bundlers inline it; the
// try covers runtimes without a `process` global.
const FREEZE_CACHED_REFS = (() => {
  try {
    return process.env.NODE_ENV !== 'production';
  } catch {
    return false;
  }
})();

const MANUAL_REF: CardSourceRef = Object.freeze({ type: 'manual' }) as CardSourceRef;

/**
 * {@link parseCardSourceRef} memoised on the source string, for hot paths that
 * parse the same deck's sources on every cards change (per-page card
 * membership). The result is SHARED between callers, so it must be treated as
 * read-only — it is frozen outside production so a mutation throws in dev
 * instead of silently corrupting every other card with the same source.
 * Bounded: past {@link CARD_SOURCE_REF_CACHE_MAX} distinct strings the oldest
 * entry is dropped.
 */
export function parseCardSourceRefCached(
  source: string | null | undefined,
): Readonly<CardSourceRef> {
  if (!source) return MANUAL_REF;
  const hit = refCache.get(source);
  if (hit) return hit;
  const ref = parseCardSourceRef(source);
  if (FREEZE_CACHED_REFS) {
    if (ref.type === 'page-rect') Object.freeze(ref.rect);
    Object.freeze(ref);
  }
  if (refCache.size >= CARD_SOURCE_REF_CACHE_MAX) {
    const oldest = refCache.keys().next();
    if (!oldest.done) refCache.delete(oldest.value);
  }
  refCache.set(source, ref);
  return ref;
}

/** Serialize a ref back into the canonical wire string. */
export function formatCardSourceRef(ref: CardSourceRef): string {
  switch (ref.type) {
    case 'text-range': {
      const inner = `m:${ref.start}-${ref.end}`;
      return ref.sourceId ? `${QUALIFIED_PREFIX}${ref.sourceId}:${inner}` : inner;
    }
    case 'page-rect': {
      const { x, y, w, h } = ref.rect;
      const inner = `r:${ref.pageNumber}:${round4(x)},${round4(y)},${round4(w)},${round4(h)}`;
      return ref.sourceId ? `${QUALIFIED_PREFIX}${ref.sourceId}:${inner}` : inner;
    }
    case 'chunk': {
      const inner = ref.start === ref.end ? `${ref.start}` : `${ref.start}-${ref.end}`;
      return ref.sourceId ? `${QUALIFIED_PREFIX}${ref.sourceId}:c:${inner}` : inner;
    }
    case 'notes':
      return ref.raw;
    case 'manual':
      return 'manual';
    case 'opaque':
      return ref.raw;
  }
}

/** Build a rect ref from absolute page coordinates. Clamps into the page. */
export function makePageRectSource(args: {
  pageNumber: number;
  /** Rect in the same units as the page dimensions below. */
  rect: { x: number; y: number; width: number; height: number };
  pageWidth: number;
  pageHeight: number;
  sourceId?: string;
}): string {
  const { pageNumber, rect, pageWidth, pageHeight, sourceId } = args;
  const safeW = pageWidth > 0 ? pageWidth : 1;
  const safeH = pageHeight > 0 ? pageHeight : 1;
  // Clamp EDGES (not width/height in isolation) so a drag that ends past
  // the page boundary yields the on-page part of the rect instead of a
  // rect overflowing the page (e.g. x=0.9 + w=0.3).
  const x1 = clamp01(rect.x / safeW);
  const y1 = clamp01(rect.y / safeH);
  const x2 = clamp01((rect.x + rect.width) / safeW);
  const y2 = clamp01((rect.y + rect.height) / safeH);
  return formatCardSourceRef({
    type: 'page-rect',
    sourceId,
    pageNumber,
    rect: {
      x: x1,
      y: y1,
      w: Math.max(0, x2 - x1),
      h: Math.max(0, y2 - y1),
    },
  });
}

/**
 * The text range of a ref, or null when it has no text anchor.
 *
 * Refs qualified to an explicit source (`s:{sourceId}:m:a-b`) return null:
 * without source context their offsets would be resolved against whatever
 * document the caller happens to hold — the pre-parser code ignored them
 * entirely (`startsWith('m:')`), and callers that DO know which source is
 * shown must use {@link textRangeForSource} instead.
 */
export function textRangeOfRef(
  ref: CardSourceRef,
): { start: number; end: number } | null {
  if (ref.type !== 'text-range') return null;
  if (ref.sourceId !== undefined) return null;
  return { start: ref.start, end: ref.end };
}

/**
 * Convenience used by render hot paths: the text range of a card source
 * RESOLVED against a given source. Qualified refs only match their own
 * source; unqualified refs match the primary source.
 */
export function textRangeForSource(
  source: string | null | undefined,
  activeSourceId: string | undefined,
  primarySourceId: string | undefined,
): { start: number; end: number } | null {
  const ref = parseCardSourceRef(source);
  if (ref.type !== 'text-range') return null;
  if (!refBelongsToSource(ref, activeSourceId, primarySourceId)) return null;
  return { start: ref.start, end: ref.end };
}

/**
 * Does `ref` point into the source identified by `sourceId`?
 * Unqualified refs belong to the deck's primary source. When the deck has
 * no recorded sources at all (legacy decks pre-backfill), every
 * unqualified ref is treated as belonging to whatever source is shown.
 */
export function refBelongsToSource(
  ref: CardSourceRef,
  sourceId: string | undefined,
  primarySourceId: string | undefined,
): boolean {
  const refSourceId = 'sourceId' in ref ? ref.sourceId : undefined;
  if (refSourceId !== undefined) return refSourceId === sourceId;
  if (primarySourceId === undefined) return true;
  return sourceId === primarySourceId;
}

/** A ref that anchors into a document — the only kind a source id applies to. */
export function isAnchoredCardSourceRef(
  ref: CardSourceRef,
): ref is Extract<CardSourceRef, { type: 'text-range' | 'page-rect' | 'chunk' }> {
  return ref.type === 'text-range' || ref.type === 'page-rect' || ref.type === 'chunk';
}

/**
 * Re-qualify an implicit ref onto an explicit source. Used when merging a
 * deck into another: the merged-in cards' unqualified refs would otherwise
 * resolve against the TARGET's primary source text — and when the primary
 * itself moves (`primary-pin.ts`). A legacy chunk ref takes its qualifiable
 * spelling (`s:{id}:c:…`). Qualified refs and non-positional refs are
 * returned unchanged.
 */
export function qualifyCardSource(
  source: string | null | undefined,
  sourceId: string,
): string | undefined {
  const ref = parseCardSourceRef(source);
  if (isAnchoredCardSourceRef(ref)) {
    if (ref.sourceId !== undefined) return source ?? undefined;
    return formatCardSourceRef({ ...ref, sourceId });
  }
  return source ?? undefined;
}

/**
 * A card source without its `s:{sourceId}:` qualifier — the form a viewer
 * that already has the card's OWN document on screen resolves against.
 *
 * Every highlight and page lookup is keyed by the bare `m:` / `r:` string of
 * the document it is drawn over (`textRangeOfRef` refuses a qualified ref on
 * purpose: without source context its offsets would land in whatever
 * document the caller holds). Once the caller HAS resolved the card to its
 * document (`materialRefForCard`), the qualifier has done its job and only
 * gets in the way. Unqualified and non-positional refs pass through.
 */
export function stripCardSourceQualifier(source: string): string {
  const ref = parseCardSourceRef(source);
  if (isAnchoredCardSourceRef(ref) && ref.sourceId) {
    return formatCardSourceRef({ ...ref, sourceId: undefined });
  }
  return source;
}

/**
 * The page rectangle of a card source, or null when it isn't rect-anchored.
 * Convenience for render hot paths that only care about rect markers.
 */
export function pageRectOfSource(
  source: string | null | undefined,
): { pageNumber: number; rect: NormalizedRect } | null {
  const ref = parseCardSourceRef(source);
  if (ref.type !== 'page-rect') return null;
  return { pageNumber: ref.pageNumber, rect: ref.rect };
}
